import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleLogin } from '@react-oauth/google';

import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
  useTheme
} from '@mui/material';

import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  ArrowForward
} from '@mui/icons-material';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const history = useHistory();
  const theme = useTheme();

  const url_login = (process.env.REACT_APP_API_BASEURL || '') + '/login';
  const url_google = (process.env.REACT_APP_API_BASEURL || '') + '/api/auth/google';

  // Shared post-login: store the session and redirect. Used by both the
  // email/password form and the "Sign in with Google" flow so they behave
  // identically (same localStorage keys read by useAuth / OnboardingProvider).
  const persistSessionAndRedirect = (data) => {
    const { token, id, email: backendEmail, message } = data || {};
    localStorage.setItem("token", token);
    localStorage.setItem("email", backendEmail || email);
    localStorage.setItem("userId", String(id));
    localStorage.setItem("authenticated", "true");
    toast.success(message || "Login successful!");
    history.push("/profile/portfolio");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!email || !password) {
      toast.error('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        url_login,
        {
          username: email,
          email: email,
          password
        },
        {
          withCredentials: true
        }
      );

      const { status, token } = response.data || {};

      const ok = (response.status === 200 || status === 200) && !!token;

      if (ok) {
        persistSessionAndRedirect(response.data);
        return;
      }

      toast.error((response.data && response.data.message) || 'Authentication failed. Please try again.');
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message;

      if (status === 403 && msg === 'Email needs to be verified') {
        toast.warning(msg);
        setTimeout(() => history.push('/verify'), 2000);
        return;
      }

      if (status === 401 && msg === 'Invalid credentials') {
        toast.error('Invalid email or password');
        return;
      }

      if (
        status === 400 &&
        (msg === 'Username is required' ||
          msg === 'Email is required' ||
          msg === 'Password is required')
      ) {
        toast.error(msg);
        return;
      }

      if (status === 500 && (msg === 'Authentication failed' || msg === 'Login failed')) {
        toast.error(msg);
        return;
      }

      toast.error(msg || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // "Sign in with Google": exchange the Google ID token for our session JWT.
  // Existing accounts only — the backend rejects unknown emails with a 400.
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        url_google,
        { credential: credentialResponse.credential },
        { withCredentials: true }
      );

      const { status, token } = response.data || {};
      const ok = (response.status === 200 || status === 200) && !!token;

      if (ok) {
        persistSessionAndRedirect(response.data);
        return;
      }

      toast.error((response.data && response.data.message) || 'Google sign-in failed.');
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message;

      if (status === 403 && msg === 'Email needs to be verified') {
        toast.warning(msg);
        setTimeout(() => history.push('/verify'), 2000);
        return;
      }

      // 400 = no account for this Google email (existing-accounts-only).
      toast.error(msg || 'Google sign-in failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error('Google sign-in was cancelled or failed.');
  };

  const handleNavigate = (path) => {
    history.push(path);
  };

  return (
    <Container component="main" maxWidth="xs">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: theme.spacing(4),
            borderRadius: 2,
            width: '100%',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 3 }}>
            Sign In
          </Typography>

          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 4,
              color: 'text.secondary'
            }}
          >
            Welcome back! Please enter your credentials to continue.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography 
                component="a"
                href="#/tempPassword"
                variant="body2" 
                sx={{ 
                  mt: 1, 
                  color: 'primary.main',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot password?
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(90deg, #3f6fb0 0%, #4d8fc7 100%)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                }
              }}
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={20} /> : <ArrowForward />}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>

            <Divider sx={{ my: 3 }}>OR</Divider>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                text="signin_with"
                shape="rectangular"
                width="320"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Typography
                component="a"
                href="#/sign-up"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Create new account
              </Typography>

              <Typography
                component="a"
                href="#/verify"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Verify account
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default SignIn;